<template>
  <section
    ref="panelRef"
    class="experiment-panel"
    :class="{ enabled: draft.experimentEnabled }"
  >
    <div class="experiment-heading">
      <div>
        <strong>AB 测试</strong>
        <a-tag :color="draft.experimentEnabled ? 'green' : undefined">
          {{ draft.experimentEnabled ? '草稿已启用' : '未启用' }}
        </a-tag>
      </div>
      <label class="experiment-switch">
        <a-switch
          :checked="!!draft.experimentEnabled"
          :disabled="disabled"
          @change="$emit('toggle', $event)"
        />
        启用实验
      </label>
    </div>

    <template v-if="draft.experimentEnabled">
      <p class="hint">按用户命中的变体，下发对应的整套 Tab 栏。</p>
      <a-form layout="vertical">
        <a-form-item
          label="AB 实验 ID"
          required
          :validate-status="fieldStatus('experimentId')"
          :help="fieldError('experimentId')"
        >
          <a-input
            :value="draft.experimentId"
            :disabled="disabled"
            data-field="experimentId"
            placeholder="请输入 AB 实验 ID，例如 10001"
            @update:value="updateField('experimentId', $event)"
          />
        </a-form-item>
        <div class="experiment-fields">
          <a-form-item
            label="实验标识"
            required
            :validate-status="fieldStatus('experimentKey')"
            :help="fieldError('experimentKey')"
          >
            <a-input
              :value="draft.experimentKey"
              :disabled="disabled"
              data-field="experimentKey"
              placeholder="例如 app_tab_navigation"
              @update:value="updateField('experimentKey', $event)"
            />
            <span class="hint">与 AB 接口的 experimentKey 一致</span>
          </a-form-item>
          <a-form-item
            label="默认配置的变体标识"
            required
            :validate-status="fieldStatus('defaultVariantKey')"
            :help="fieldError('defaultVariantKey')"
          >
            <a-input
              :value="draft.defaultVariantKey"
              :disabled="disabled"
              data-field="defaultVariantKey"
              placeholder="请输入默认变体标识"
              @update:value="updateField('defaultVariantKey', $event)"
            />
            <span class="hint">对照组与未命中用户使用默认配置</span>
          </a-form-item>
        </div>
      </a-form>
      <p class="hint">
        流量及用户分组由 AB
        服务管理。关闭实验、未命中或接口异常时，统一下发默认配置。修改发布后生效。
      </p>
      <div class="variant-heading">
        <span
          ><strong>配置变体</strong> 1 套默认 +
          {{ draft.variants.length - 1 }} 个变体</span
        >
        <a-button :disabled="disabled" @click="$emit('add')"
          >+ 添加变体</a-button
        >
      </div>
      <div class="variant-cards">
        <button
          v-for="variant in draft.variants"
          :key="variant.uiId"
          type="button"
          :disabled="disabled"
          class="variant-card"
          :class="{
            active: activeVariantId === variant.uiId,
            invalid: error?.variantId === variant.uiId
          }"
          @click="$emit('select', variant.uiId)"
        >
          <strong>{{ variant.variantName }}</strong>
          <a-tag v-if="variant.isDefault === 1">兜底</a-tag>
          <span
            >{{ variant.variantKey || '标识未填写' }} ·
            {{ variant.items.length }} 个 Tab</span
          >
        </button>
      </div>
      <div v-if="activeVariant" class="active-variant">
        <div>
          <strong>正在编辑：{{ activeVariant.variantName }}</strong>
          <span class="hint"
            >排序、默认选中与移除仅影响当前变体；编辑物料内容会同步所有引用。</span
          >
        </div>
        <a-tag v-if="activeVariant.isDefault === 1">默认配置不可删除</a-tag>
        <a-space v-else>
          <a-button
            size="small"
            :disabled="disabled"
            @click="$emit('edit', activeVariant)"
            >编辑变体</a-button
          >
          <a-button
            size="small"
            danger
            :disabled="disabled"
            @click="$emit('remove', activeVariant)"
            >删除变体</a-button
          >
        </a-space>
      </div>
    </template>
  </section>
</template>

<script>
import { computed, ref } from 'vue'

export default {
  props: {
    draft: { type: Object, required: true },
    activeVariantId: String,
    disabled: Boolean,
    error: { type: Object, default: null }
  },
  emits: ['toggle', 'field-change', 'add', 'select', 'edit', 'remove'],
  setup(props, { emit, expose }) {
    const panelRef = ref()
    const activeVariant = computed(() =>
      props.draft.variants.find(item => item.uiId === props.activeVariantId)
    )
    const fieldError = field =>
      props.error?.field === field ? props.error.message : undefined
    const fieldStatus = field => (fieldError(field) ? 'error' : undefined)
    const updateField = (field, value) => emit('field-change', { field, value })
    expose({
      focusField(field) {
        const input = panelRef.value?.querySelector(`[data-field="${field}"]`)
        input?.scrollIntoView({ block: 'nearest' })
        input?.focus()
      }
    })
    return { activeVariant, fieldError, fieldStatus, panelRef, updateField }
  }
}
</script>

<style lang="scss" scoped>
.experiment-panel {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  &.enabled {
    background: #f7fbff;
  }
}
.experiment-heading,
.variant-heading,
.active-variant {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.experiment-heading strong {
  margin-right: 12px;
}
.experiment-switch {
  display: flex;
  gap: 8px;
  align-items: center;
}
.hint {
  display: block;
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 8px;
}
.experiment-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.variant-heading {
  margin-top: 20px;
  color: #595959;
}
.variant-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 12px 0;
}
.variant-card {
  width: 180px;
  max-width: 100%;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;
  strong {
    margin-right: 6px;
  }
  > span:last-child {
    display: block;
    margin-top: 6px;
    color: #8c8c8c;
    font-size: 12px;
  }
  &.active {
    border-color: #1890ff;
    background: #e6f7ff;
  }
  &.invalid {
    border-color: #ff4d4f;
  }
  &:disabled {
    cursor: not-allowed;
  }
}
.active-variant {
  padding-top: 12px;
  border-top: 1px solid #e8e8e8;
}
</style>
